import { useEffect, useRef, useState } from 'react'
import { Search } from 'lucide-react'
import Select from 'components/Inputs/Select'

/*
  Busca no servidor (cliente, conta de destino), sobre o `Select` do site, com as props do
  react-select/async de antes do Design System. O contrato das opções, do `onChange` e do erro é o
  do `Select`.

  - `loadOptions(texto)` devolve as opções, numa promise ou não. Ela só é chamada depois que a
    digitação para por `debounceDelay` ms, com a função mais recente, porque as telas a recriam a
    cada render. Uma resposta que chega depois de outra busca é descartada;
  - a lista abre com a busca vazia, que não busca nada, como o react-select sem `defaultOptions`.
    Enquanto a busca espera, a lista mostra `loadingMessage`;
  - o texto digitado vai para o `onInputChange`, e `onMenuOpen` e `onMenuClose` vêm quando a
    lista abre e fecha. A busca de cliente do agendamento guarda assim o texto para o "Novo cliente";
  - a escolhida de antes (a edição que já vem com o cliente) mostra o `label` dela;
  - `inputId` vira o `id` da caixa, para a tela pôr o foco nela.

  A busca recomeça vazia a cada vez que a lista abre, então `inputValue`, `onFocus`,
  `blurInputOnSelect` e `autoFocus` não têm mais efeito. A lupa é a do Design System.
*/
export default function SelectAsync({
  loadOptions,
  options,
  debounceDelay = 500,
  loadingMessage = 'Buscando opções',
  onInputChange,
  inputId,
  id,
  placeholder,
  'aria-label': ariaLabel,
  ...props
}) {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const loadOptionsRef = useRef(loadOptions)
  const timeout = useRef(null)
  const lastSearch = useRef(0)

  useEffect(() => {
    loadOptionsRef.current = loadOptions
  })

  useEffect(() => () => {
    clearTimeout(timeout.current)
    lastSearch.current = -1
  }, [])

  const handleSearchChange = text => {
    onInputChange?.(text)
    clearTimeout(timeout.current)
    const search = ++lastSearch.current

    if (!text.trim()) {
      setResults([])
      setLoading(false)
      return
    }

    setLoading(true)
    timeout.current = setTimeout(() => {
      Promise.resolve()
        .then(() => loadOptionsRef.current(text))
        .catch(() => [])
        .then(found => {
          if (search !== lastSearch.current) return
          setResults(Array.isArray(found) ? found : [])
          setLoading(false)
        })
    }, debounceDelay)
  }

  return (
    <Select
      {...props}
      placeholder={placeholder}
      id={id ?? inputId}
      // O `inputId` não tem <label> que aponte para ele: o nome vem do placeholder.
      aria-label={ariaLabel ?? (id ? undefined : placeholder)}
      icon={Search}
      options={loadOptions ? results : options}
      onSearchChange={loadOptions ? handleSearchChange : undefined}
      loading={loading}
      loadingMessage={loadingMessage}
    />
  )
}
